import dayjs from 'dayjs'

import { ApiClient, clients } from 'lib/axios'
import { refreshAccessToken, selectRefreshAccessToken } from 'store/ducks/auth'
import { recaptchaApi } from 'store/api/yay/recaptcha'
import { addError } from 'store/slices/recaptcha'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { ApiErrorResponse } from 'lib/_types'

type AxiosBaseQueryProps<T1, T2> = {
  client?: ApiClient
  transformResponse?: (res: T2) => T1
}

export const axiosBaseQuery =
  <ResultType = any, ResponseType = ResultType>({
    client = 'default',
    transformResponse,
  }: AxiosBaseQueryProps<ResultType, ResponseType>) =>
  async (arg: AxiosRequestConfig, api: BaseQueryApi) => {
    const requestConfig: AxiosRequestConfig = {
      method: 'GET',
      // to make it able to custom validation
      validateStatus: (status) => status <= 500,
      ...arg,
    }

    let response = await clients[client].client.request<
      ResponseType | ApiErrorResponse
    >(requestConfig)

    switch ((response?.data as any)?.error_code) {
      // access token expire
      case -3: {
        response = await onAccessTokenExpired(api, client, requestConfig)
        break
      }

      // recaptcha require
      case -29: {
        response = await onRecaptchaRequire(api, client, requestConfig)
        break
      }

      default:
        break
    }

    // failed
    if (response.status >= 400) {
      return {
        error: {
          status: response.status,
          data: response.data as ApiErrorResponse,
        },
      }
    }

    return {
      data: transformResponse
        ? transformResponse(response.data as ResponseType)
        : (response.data as unknown as ResultType),
    }
  }

const onAccessTokenExpired = (
  api: BaseQueryApi,
  client: ApiClient,
  requestConfig: AxiosRequestConfig
) =>
  promiseIntervalPolling(async (resolve) => {
    const refreshTokenState = selectRefreshAccessToken(api.getState())

    const isAfterOneMinute = dayjs().isAfter(
      dayjs(refreshTokenState?.data?.created_at * 1000).add(1, 'minutes')
    )

    // access token was refreshed but token refreshed more than 1 minutes ago or refreshTokenState haven't fetched before
    if (
      (refreshTokenState?.isLoaded && isAfterOneMinute) ||
      (!refreshTokenState?.isLoaded && !refreshTokenState?.isLoading)
    ) {
      // then refresh the access token
      api.dispatch(refreshAccessToken())
    } else if (refreshTokenState.isLoaded) {
      // replay request
      const result = await clients[client].client.request(requestConfig)
      // unlock blocking promise
      resolve(result)
    }
  }, true)

const onRecaptchaRequire = async (
  api: BaseQueryApi,
  client: ApiClient,
  requestConfig: AxiosRequestConfig
) => {
  // trigger recaptcha
  api.dispatch(addError({ id: requestConfig.url }))

  return promiseIntervalPolling(async (resolve) => {
    if (
      recaptchaApi.endpoints.verifyRecaptcha.select('single')(
        api.getState() as any
      ).isSuccess
    ) {
      // replay request
      const result = await clients[client].client.request(requestConfig)
      // unlock blocking promise
      resolve(result)
    }
  })
}

const promiseIntervalPolling = async (
  fn: (resolve: (result: AxiosResponse) => void) => Promise<void>,
  doFirstRun?: boolean
) => {
  // for interval tracking
  let i: NodeJS.Timer = null

  // polling check until access token is updated
  const result = await new Promise<AxiosResponse>((resolve) => {
    if (doFirstRun) fn(resolve)
    i = setInterval(() => fn(resolve), 500)
  })

  // stop polling logic
  clearInterval(i)

  return result
}

export const axiosShortQuery =
  <ResultType = any, RequestType = any, ResponseType = ResultType>(
    config: AxiosRequestConfig | ((params: RequestType) => AxiosRequestConfig),
    client: ApiClient = 'default',
    transformResponse?: (res: ResponseType) => ResultType
  ) =>
  (params: RequestType, api: BaseQueryApi) =>
    axiosBaseQuery<ResultType, ResponseType>({ client, transformResponse })(
      typeof config === 'function' ? config(params) : config,
      api
    )
